import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const ISPEditCustomer = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [customerData, setCustomerData] = useState({
    company_name: '',
    contact_person: '',
    mobile: '',
    gst_number: '',
    installation_address: '',
    static_ip: '',
    account_manager: '',
    status: 'active'
  });

  const [planData, setPlanData] = useState({
    plan_name: '',
    download_speed: '',
    upload_speed: '',
    plan_cost: '',
    activation_date: '',
    expiry_date: ''
  });

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  const fetchCustomer = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/customers', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const customers = await response.json();
        const customer = customers.find(c => c.customer_id === customerId);
        
        if (customer) {
          setCustomerData({
            company_name: customer.company_name,
            contact_person: customer.contact_person,
            mobile: customer.mobile,
            gst_number: customer.gst_number || '',
            installation_address: customer.installation_address,
            static_ip: customer.static_ip || '',
            account_manager: customer.account_manager || '',
            status: customer.status
          });

          if (customer.plan) {
            setPlanData({
              plan_name: customer.plan.plan_name,
              download_speed: customer.plan.download_speed,
              upload_speed: customer.plan.upload_speed,
              plan_cost: customer.plan.plan_cost,
              activation_date: customer.plan.activation_date?.split('T')[0] || '',
              expiry_date: customer.plan.expiry_date?.split('T')[0] || ''
            });
          }
        }
      }
      setLoading(false);
    } catch (error) {
      toast.error('Error loading customer');
      setLoading(false);
    }
  };

  const handleCustomerChange = (e) => {
    setCustomerData({ ...customerData, [e.target.name]: e.target.value });
  };

  const handlePlanChange = (e) => {
    setPlanData({ ...planData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`/api/admin/customers/${customerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(customerData)
      });

      if (response.ok) {
        toast.success('Customer updated successfully!');
        navigate('/isp/users');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error updating customer');
      }
    } catch (error) {
      toast.error('Error updating customer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-6 px-4 shadow-lg">
        <div className="container mx-auto">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/isp/users')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Edit Customer</h1>
              <p className="text-blue-200 mt-1">Customer ID: {customerId}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle>Customer Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    name="company_name"
                    value={customerData.company_name}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>Contact Person *</Label>
                  <Input
                    name="contact_person"
                    value={customerData.contact_person}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>Mobile Number *</Label>
                  <Input
                    name="mobile"
                    value={customerData.mobile}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>GST Number</Label>
                  <Input
                    name="gst_number"
                    value={customerData.gst_number}
                    onChange={handleCustomerChange}
                  />
                </div>

                <div>
                  <Label>Installation Address *</Label>
                  <Input
                    name="installation_address"
                    value={customerData.installation_address}
                    onChange={handleCustomerChange}
                    required
                  />
                </div>

                <div>
                  <Label>Static IP</Label>
                  <Input
                    name="static_ip"
                    value={customerData.static_ip}
                    onChange={handleCustomerChange}
                  />
                </div>

                <div>
                  <Label>Account Manager</Label>
                  <Input
                    name="account_manager"
                    value={customerData.account_manager}
                    onChange={handleCustomerChange}
                  />
                </div>

                <div>
                  <Label>Status *</Label>
                  <select
                    name="status"
                    value={customerData.status}
                    onChange={handleCustomerChange}
                    className="w-full border rounded-md px-3 py-2"
                    required
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle>Plan Information (View Only)</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label>Plan Name</Label>
                  <Input value={planData.plan_name} disabled />
                </div>

                <div>
                  <Label>Download Speed</Label>
                  <Input value={planData.download_speed} disabled />
                </div>

                <div>
                  <Label>Upload Speed</Label>
                  <Input value={planData.upload_speed} disabled />
                </div>

                <div>
                  <Label>Plan Cost (₹)</Label>
                  <Input value={planData.plan_cost} disabled />
                </div>

                <div>
                  <Label>Activation Date</Label>
                  <Input value={planData.activation_date} disabled />
                </div>

                <div>
                  <Label>Expiry Date</Label>
                  <Input value={planData.expiry_date} disabled />
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-6"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {saving ? 'Saving Changes...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ISPEditCustomer;
